import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  Cake,
  Clock,
  FlaskConical,
  History,
  LineChart,
  Loader2,
  MessagesSquare,
  Mic,
  Paperclip,
  PawPrint,
  RefreshCw,
  Scissors,
  Send,
  Sparkles,
  Star,
  Trash2,
  Weight,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  discussoesControllerObterArquivoDaMensagem,
  discussoesControllerObterArquivoDoExame,
  getDiscussoesControllerListarMensagensQueryKey,
  useDiscussoesControllerAvaliar,
  useDiscussoesControllerCriarExameExterno,
  useDiscussoesControllerDetalhar,
  useDiscussoesControllerEncerrar,
  useDiscussoesControllerEnviarAnexo,
  useDiscussoesControllerEnviarMensagem,
  useDiscussoesControllerEnviarMensagemDeVoz,
  useDiscussoesControllerGerarResumo,
  useDiscussoesControllerListarMensagens,
  useDiscussoesControllerMarcarComoLida,
  useDiscussoesControllerObterPacienteDaDiscussao,
  useDiscussoesControllerObterRascunho,
} from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { ApiError } from '@/api/http-client';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AberturaDiscussaoWizard } from '@/features/discussoes/components/AberturaDiscussaoWizard';
import { ArtigosDoAdvisor } from '@/features/discussoes/components/ArtigosDoAdvisor';
import { EtapaPagamento } from '@/features/discussoes/components/EtapaPagamento';
import { EvolucaoDoPacienteDialogo } from '@/features/discussoes/components/EvolucaoDoPacienteDialogo';
import { HistoricoDoPacienteDialogo } from '@/features/discussoes/components/HistoricoDoPacienteDialogo';
import { ResultadosDoExameDialogo } from '@/features/discussoes/components/ResultadosDoExameDialogo';
import { MensagemDeVoz } from '@/features/discussoes/components/MensagemDeVoz';
import { SugestaoDeTexto } from '@/features/discussoes/components/SugestaoDeTexto';
import { VisualizadorDeDocumento } from '@/features/discussoes/components/VisualizadorDeDocumento';
import { useFluxoDeAberturaDeDiscussao } from '@/features/discussoes/hooks/useFluxoDeAberturaDeDiscussao';
import {
  DURACAO_MAXIMA_SEGUNDOS,
  formatarDuracao,
  type Gravacao,
  useGravadorDeAudio,
} from '@/shared/hooks/useGravadorDeAudio';
import {
  classeDaDiscussao,
  classeUrgenciaPrazo,
  diasParaEncerramentoAutomatico,
  rotuloDaDiscussao,
  rotuloPrazoEncerramento,
} from '@/features/discussoes/rotulos';
import { CORES_SEXO, ESPECIES, formatarIdade, SEXOS } from '@/features/pacientes/lib/especies';
import { Avatar } from '@/shared/components/Avatar';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { ROTAS } from '@/shared/lib/rotas';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Textarea } from '@/shared/ui/textarea';
import { toast } from '@/shared/ui/sonner';
import {
  adicionarAnexos,
  arrastoTemArquivos,
  FORMATOS_DE_ANEXO,
} from '@/features/discussoes/anexosDoChat';
import { useQueryClient } from '@tanstack/react-query';

/**
 * 2s: sem WebSocket ainda, é o polling que faz o chat parecer vivo. Mensagem
 * e status (ex.: ABERTA → EM_ATENDIMENTO) só chegam de verdade pro outro
 * lado no próximo tick — 2s é o equilíbrio entre "parece ao vivo" e não
 * martelar o servidor.
 */
const INTERVALO_POLLING_MENSAGENS_MS = 2000;

const schemaAvaliacao = z.object({
  nota: z.coerce.number().min(1).max(5),
  resolvido: z.enum(['sim', 'nao']),
  descricao: z.string().trim().max(1000),
});
type FormularioAvaliacao = z.input<typeof schemaAvaliacao>;

export function DiscussaoDetalhePage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [corpo, setCorpo] = useState('');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [arrastando, setArrastando] = useState(false);
  /** "Enviando 2 de 3" — cada anexo vira uma mensagem, e a fila pode demorar. */
  const [progressoDoEnvio, setProgressoDoEnvio] = useState<{ atual: number; total: number } | null>(
    null,
  );
  const [confirmandoEncerrar, setConfirmandoEncerrar] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [evolucaoAberta, setEvolucaoAberta] = useState(false);
  const [avaliando, setAvaliando] = useState(false);
  const [abrindoAnexoId, setAbrindoAnexoId] = useState<string | null>(null);
  const [visualizando, setVisualizando] = useState<{
    url: string | null;
    mimeType: string | null;
  } | null>(null);
  const fimDaListaRef = useRef<HTMLDivElement>(null);
  const inputArquivoRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Rascunho congelado no clique de "Melhorar texto" — `null` = painel fechado.
  // Enquanto ele está aberto o composer fica travado: um rascunho de cada vez.
  const [textoParaMelhorar, setTextoParaMelhorar] = useState<string | null>(null);
  // Remonta o painel a cada abertura (e com isso refaz o pedido à IA).
  const [aberturaDaSugestao, setAberturaDaSugestao] = useState(0);
  // 503 uma vez = o ambiente não tem IA; o botão some até recarregar a tela.
  const [iaDeTextoIndisponivel, setIaDeTextoIndisponivel] = useState(false);

  const {
    data: discussao,
    isPending,
    isError,
    error,
    refetch,
  } = useDiscussoesControllerDetalhar(id, {
    query: { refetchInterval: INTERVALO_POLLING_MENSAGENS_MS },
  });

  const emAndamento = discussao?.status === 'ABERTA' || discussao?.status === 'EM_ATENDIMENTO';

  const { data: mensagens } = useDiscussoesControllerListarMensagens(
    id,
    { pagina: 1, porPagina: 100, ordenarPor: 'criadoEm', ordem: 'asc' },
    {
      query: {
        enabled: Boolean(discussao) && discussao?.status !== 'RASCUNHO',
        refetchInterval: emAndamento ? INTERVALO_POLLING_MENSAGENS_MS : false,
      },
    },
  );

  const { data: paciente } = useDiscussoesControllerObterPacienteDaDiscussao(id, {
    query: { enabled: Boolean(discussao?.pacienteId) },
  });

  const resumoIa = useDiscussoesControllerGerarResumo({
    mutation: {
      onError: (e) => {
        toast.error(
          e instanceof ApiError && e.codigo === 'CONVERSA_MUITO_CURTA'
            ? 'Ainda não há mensagens suficientes para gerar um resumo útil.'
            : 'Não foi possível gerar o resumo agora.',
        );
      },
    },
  });

  const [abrindoExameId, setAbrindoExameId] = useState<string | null>(null);
  const [exameInternoId, setExameInternoId] = useState<string | null>(null);

  const examesInternos = discussao?.exames.filter((e) => !e.externo) ?? [];
  const examesExternos = discussao?.exames.filter((e) => e.externo) ?? [];

  // O advisor não leva o documento embora: vê num visualizador dentro do
  // app, sem link cru nem botão de download. O médico é dono do dado — para
  // ele, abre normal numa aba nova.
  const souAdvisorDestaDiscussao = discussao?.assessorId === me?.usuario.id;

  const enviarMensagem = useDiscussoesControllerEnviarMensagem();
  const marcarComoLida = useDiscussoesControllerMarcarComoLida();
  const encerrar = useDiscussoesControllerEncerrar();
  const enviarAnexo = useDiscussoesControllerEnviarAnexo();
  const criarExameExterno = useDiscussoesControllerCriarExameExterno();
  const enviarVoz = useDiscussoesControllerEnviarMensagemDeVoz();
  const enviando =
    enviarMensagem.isPending ||
    enviarAnexo.isPending ||
    criarExameExterno.isPending ||
    enviarVoz.isPending;

  const gravador = useGravadorDeAudio({
    aoAtingirLimite: (gravacao) => {
      toast.info('Limite de 5 minutos atingido — o áudio foi enviado.');
      void aoEnviarVoz(gravacao);
    },
    aoFalhar: (erro) => {
      if (erro === 'permissao-negada') {
        // Bloqueado antes, o navegador NÃO pergunta de novo — só a pessoa
        // libera, pelo cadeado. Por isso o passo a passo, e não "permita".
        toast.error('O microfone está bloqueado para este site.', {
          description:
            'Clique no cadeado ao lado do endereço, em "Microfone" escolha "Permitir" e recarregue a página.',
          duration: 12_000,
        });
      } else if (erro === 'silencio') {
        toast.error('Não captamos som do microfone — o áudio não foi enviado.', {
          description: 'Verifique se o microfone está ligado, sem mudo, e selecionado no navegador.',
          duration: 10_000,
        });
      } else {
        toast.error(erro === 'sem-microfone' ? 'Nenhum microfone encontrado.' : 'Não foi possível gravar o áudio.');
      }
    },
  });

  /**
   * Mensagem de voz. Diferente do anexo, o áudio NÃO vira exame externo no
   * prontuário — é conversa, não documento clínico. `clientMsgId` fixo por
   * gravação: o "Tentar de novo" do toast reenvia com o MESMO id, e o backend
   * devolve a mensagem original se a primeira tentativa tinha chegado.
   */
  async function aoEnviarVoz(gravacao: Gravacao, clientMsgId: string = crypto.randomUUID()) {
    try {
      await enviarVoz.mutateAsync({
        id,
        data: { audio: gravacao.audio, clientMsgId, duracaoSegundos: gravacao.duracaoSegundos },
      });
      await queryClient.invalidateQueries({
        queryKey: getDiscussoesControllerListarMensagensQueryKey(id),
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível enviar o áudio.', {
        action: { label: 'Tentar de novo', onClick: () => void aoEnviarVoz(gravacao, clientMsgId) },
        duration: 15_000,
      });
    }
  }

  async function finalizarGravacao() {
    const gravacao = await gravador.finalizar();
    if (gravacao) await aoEnviarVoz(gravacao);
  }

  useEffect(() => {
    if (discussao && discussao.naoLidas > 0) void marcarComoLida.mutateAsync({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, discussao?.naoLidas]);

  useEffect(() => {
    fimDaListaRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens?.itens.length]);

  // Gera sozinho assim que a conversa tem mensagem suficiente pra valer a
  // pena — só uma vez por id (chamar de novo a cada render reenviaria a
  // cada nova mensagem chegando pelo polling).
  const totalMensagens = mensagens?.itens.length ?? 0;
  const resumoIaMutate = resumoIa.mutate;
  useEffect(() => {
    if (id && totalMensagens >= 3) resumoIaMutate({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, totalMensagens >= 3]);

  /** Soma arquivos à fila do composer (clipe ou arrastar) e avisa o que ficou de fora. */
  function anexar(novos: Iterable<File>) {
    const { arquivos: fila, recusados } = adicionarAnexos(arquivos, novos);
    setArquivos(fila);
    const [unico] = recusados;
    if (recusados.length === 1 && unico) {
      toast.error(`${unico.nome}: ${unico.motivo}.`);
    } else if (recusados.length > 1) {
      toast.error(`${recusados.length} arquivos não foram anexados.`, {
        description: recusados.map((r) => `${r.nome}: ${r.motivo}`).join('\n'),
      });
    }
  }

  /**
   * Envia texto e anexos.
   *
   * ⚠️ UMA MENSAGEM POR ARQUIVO: a mensagem do servidor tem um `arquivoId` só.
   * O texto digitado vai junto com o primeiro anexo; os demais saem como
   * "📎 nome". Se algo falha no meio, o que já saiu fica no chat e só o que
   * falta volta para o composer — reenviar não duplica o que já foi.
   */
  async function aoEnviar() {
    const texto = corpo.trim();
    if (!texto && arquivos.length === 0) return;

    const fila = [...arquivos];
    /** Quantos da fila já viraram mensagem — o resto volta ao composer se algo falhar. */
    let enviados = 0;
    let textoPendente = texto;
    setCorpo('');
    setArquivos([]);
    try {
      if (fila.length === 0) {
        await enviarMensagem.mutateAsync({
          id,
          data: { corpo: texto, clientMsgId: crypto.randomUUID() },
        });
        textoPendente = '';
      }

      for (const arquivo of fila) {
        setProgressoDoEnvio({ atual: enviados + 1, total: fila.length });

        const anexo = await enviarAnexo.mutateAsync({ data: { arquivo } });

        // Anexo de chat também vira documento externo no prontuário — só dá
        // pra vincular a um paciente se a discussão tiver um.
        //
        // ⚠️ E só para quem é do AMBIENTE da discussão. O advisor tem tenant
        // próprio: o paciente não existe para ele, o servidor responde 404
        // "Paciente não encontrado" e o `catch` abortava a mensagem inteira.
        // O anexo do advisor vai só na mensagem — não é exame do paciente.
        if (discussao?.pacienteId && discussao.tenantId === me?.tenant.id) {
          await criarExameExterno.mutateAsync({
            data: {
              pacienteId: discussao.pacienteId,
              coletadoEm: new Date().toISOString(),
              arquivoId: anexo.arquivoId,
              discussaoId: id,
            },
          });
        }

        await enviarMensagem.mutateAsync({
          id,
          data: {
            corpo: textoPendente || `📎 ${arquivo.name}`,
            clientMsgId: crypto.randomUUID(),
            arquivoId: anexo.arquivoId,
          },
        });
        textoPendente = '';
        enviados += 1;
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível enviar a mensagem.');
      setCorpo(textoPendente);
      setArquivos(fila.slice(enviados));
    } finally {
      setProgressoDoEnvio(null);
      await queryClient.invalidateQueries({
        queryKey: getDiscussoesControllerListarMensagensQueryKey(id),
      });
    }
  }


  function abrirSugestaoDeTexto() {
    const texto = corpo.trim();
    if (!texto) return;
    setTextoParaMelhorar(texto);
    setAberturaDaSugestao((n) => n + 1);
  }

  function fecharSugestaoDeTexto() {
    setTextoParaMelhorar(null);
    requestAnimationFrame(() => composerRef.current?.focus());
  }

  async function aoAbrirAnexo(mensagemId: string) {
    setAbrindoAnexoId(mensagemId);
    try {
      const { url, mimeType } = await discussoesControllerObterArquivoDaMensagem(id, mensagemId);
      if (!url) toast.error('Arquivo não encontrado.');
      else if (souAdvisorDestaDiscussao) setVisualizando({ url, mimeType });
      else window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível abrir o anexo.');
    } finally {
      setAbrindoAnexoId(null);
    }
  }

  async function aoAbrirExame(exameId: string) {
    setAbrindoExameId(exameId);
    try {
      const { url, mimeType } = await discussoesControllerObterArquivoDoExame(exameId);
      if (!url) toast.error('Arquivo não encontrado.');
      else if (souAdvisorDestaDiscussao) setVisualizando({ url, mimeType });
      else window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível abrir o exame.');
    } finally {
      setAbrindoExameId(null);
    }
  }

  async function aoEncerrar() {
    try {
      await encerrar.mutateAsync({ id });
      toast.success('Discussão encerrada.');
      // Só quem encerra manualmente é o autor — e é ele quem avalia. Pede a
      // nota na hora, em vez de deixar só o banner esperando um clique extra.
      setAvaliando(true);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível encerrar.');
    } finally {
      setConfirmandoEncerrar(false);
    }
  }

  if (isPending) return <EstadoCarregando />;

  if (isError || !discussao) {
    const naoEncontrada = error instanceof ApiError && error.status === 404;
    return naoEncontrada ? (
      <EstadoVazio
        icone={MessagesSquare}
        titulo="Discussão não encontrada"
        descricao="Ela pode ter sido removida, ou você não participa dela."
        acao={
          <Button asChild variant="outline">
            <Link to={ROTAS.discussoes}>Ver discussões</Link>
          </Button>
        }
      />
    ) : (
      <EstadoErro aoTentarDeNovo={() => void refetch()} />
    );
  }

  const diasParaEncerrar = diasParaEncerramentoAutomatico(discussao.prazoAutoEncerramento);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Link
        to={ROTAS.discussoes}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Discussões técnicas
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        <div className="lg:col-span-2 bg-surface-raised rounded-lg border-2 border-brand/20 p-6 space-y-3 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-mono text-xs text-muted-foreground">{discussao.codigo}</p>
              <h1 className="text-lg font-semibold text-foreground">{discussao.assunto}</h1>
            </div>
            <Badge variant="outline" className={classeDaDiscussao(discussao)}>
              {rotuloDaDiscussao(discussao)}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Médico: </span>
              <span className="text-foreground">{discussao.autorNome}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Advisor: </span>
              {/* `null` só é possível num rascunho ainda antes da etapa
                  "Conexão com especialista" do wizard web. */}
              <span className="text-foreground">{discussao.assessorNome ?? 'Ainda não escolhido'}</span>
            </div>
            {discussao.especialidadeNome && (
              <div>
                <span className="text-muted-foreground">Especialidade: </span>
                <span className="text-foreground">{discussao.especialidadeNome}</span>
              </div>
            )}
            {diasParaEncerrar !== null && (
              <div className={`flex items-center gap-1.5 ${classeUrgenciaPrazo(diasParaEncerrar)}`}>
                <Clock className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium">{rotuloPrazoEncerramento(diasParaEncerrar)}</span>
              </div>
            )}
          </div>

          {(discussao.status === 'ABERTA' || discussao.status === 'EM_ATENDIMENTO') &&
            discussao.autorId === me?.usuario.id && (
              <div className="pt-2 mt-auto">
                <Button variant="outline" size="sm" onClick={() => setConfirmandoEncerrar(true)}>
                  Encerrar discussão
                </Button>
              </div>
            )}
        </div>

        {paciente && (
          <div className="bg-surface-raised rounded-lg border border-brand/20 p-4 overflow-y-auto min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <span
                className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${CORES_SEXO[paciente.sexo].fundo}`}
              >
                <PawPrint className={`w-5 h-5 ${CORES_SEXO[paciente.sexo].texto}`} />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-foreground text-sm truncate">{paciente.nome}</p>
                <p className="text-xs text-muted-foreground">
                  {ESPECIES[paciente.especie]} · {paciente.raca ?? 'Raça não cadastrada'}
                </p>
              </div>
              <Badge
                variant="outline"
                className={`text-xs font-semibold ml-auto shrink-0 ${CORES_SEXO[paciente.sexo].texto}`}
              >
                {SEXOS[paciente.sexo]}
              </Badge>
            </div>

            <ul className="space-y-1.5 mb-3">
              {[
                {
                  icone: Cake,
                  texto:
                    formatarIdade(paciente.idadeMeses) === '—'
                      ? 'Idade não cadastrada'
                      : formatarIdade(paciente.idadeMeses),
                },
                {
                  icone: Weight,
                  texto: paciente.pesoKg === null ? 'Peso não cadastrado' : `${paciente.pesoKg} kg`,
                },
                { icone: Scissors, texto: paciente.castrado ? 'Castrado' : 'Não castrado' },
              ].map(({ icone: Icone, texto }) => (
                <li key={texto} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icone className="w-3.5 h-3.5 shrink-0" />
                  {texto}
                </li>
              ))}
            </ul>

            <p className="text-xs text-muted-foreground border-t border-border pt-2 mb-2 break-words">
              {paciente.observacoes ?? 'Sem observações cadastradas'}
            </p>

            <div className="pt-2 border-t border-border">
              <p className="text-xs font-semibold uppercase tracking-wide mb-0.5 text-muted-foreground">
                Responsável
              </p>
              <p className="text-sm font-medium text-foreground truncate">
                {paciente.tutorNome ?? '—'}
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full mt-3"
              onClick={() => setHistoricoAberto(true)}
            >
              <History className="w-4 h-4" />
              Ver histórico do paciente
            </Button>
          </div>
        )}
      </div>

      {discussao.status === 'RASCUNHO' ? (
        <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6">
          <RascunhoDaDiscussao discussaoId={id} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 bg-surface-raised rounded-lg border-2 border-brand/20 flex flex-col h-[32rem]">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {mensagens?.itens.length === 0 ? (
                <EstadoVazio
                  icone={MessagesSquare}
                  titulo="Nenhuma mensagem ainda"
                  descricao="Envie a primeira mensagem para o advisor."
                />
              ) : (
                mensagens?.itens.map((mensagem) => {
                  const doAdvisor = mensagem.autorId === discussao.assessorId;
                  return (
                    <div
                      key={mensagem.id}
                      className={`flex items-start gap-2 max-w-[80%] ${doAdvisor ? 'mr-auto' : 'ml-auto flex-row-reverse'}`}
                    >
                      <Avatar
                        fotoUrl={mensagem.autorFotoUrl}
                        nome={mensagem.autorNome}
                        className="w-7 h-7 mt-0.5"
                      />

                      <div
                        className={`rounded-lg px-3 py-2 ${doAdvisor ? 'bg-brand/10' : 'bg-muted/60'}`}
                      >
                        <p className="text-xs font-medium text-muted-foreground">
                          {mensagem.autorNome}
                        </p>
                        {mensagem.voz ? (
                          // O `corpo` ("🎤 " + transcrição) é para quem não toca
                          // áudio (App, notificação) — aqui o balão mostra o
                          // player e a transcrição no lugar dele.
                          <MensagemDeVoz
                            discussaoId={id}
                            mensagemId={mensagem.id}
                            voz={mensagem.voz}
                            aoTranscrever={() =>
                              void queryClient.invalidateQueries({
                                queryKey: getDiscussoesControllerListarMensagensQueryKey(id),
                              })
                            }
                          />
                        ) : (
                          <p className="text-sm text-foreground whitespace-pre-wrap">
                            {mensagem.corpo}
                          </p>
                        )}
                        {mensagem.arquivoId && !mensagem.voz && (
                          <button
                            type="button"
                            onClick={() => void aoAbrirAnexo(mensagem.id)}
                            disabled={abrindoAnexoId === mensagem.id}
                            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline disabled:opacity-60"
                          >
                            {abrindoAnexoId === mensagem.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Paperclip className="w-3.5 h-3.5" />
                            )}
                            Ver anexo
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={fimDaListaRef} />
            </div>

            {discussao.status !== 'ENCERRADA' && discussao.status !== 'CANCELADA' && (
              <div className="border-t border-brand/10 p-3 space-y-2">
                {arquivos.length > 0 && (
                  <ul aria-label="Anexos a enviar" className="space-y-1">
                    {arquivos.map((a) => (
                      <li
                        key={`${a.name}-${a.size}-${a.lastModified}`}
                        className="flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-foreground"
                      >
                        <Paperclip className="w-3.5 h-3.5 shrink-0 text-brand" />
                        <span className="truncate flex-1">{a.name}</span>
                        <button
                          type="button"
                          onClick={() => setArquivos((atuais) => atuais.filter((x) => x !== a))}
                          disabled={enviando}
                          aria-label={`Remover ${a.name}`}
                          className="text-muted-foreground hover:text-foreground shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {progressoDoEnvio && progressoDoEnvio.total > 1 && (
                  <div role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Enviando anexo {progressoDoEnvio.atual} de {progressoDoEnvio.total}...
                  </div>
                )}
                {textoParaMelhorar !== null && (
                  <SugestaoDeTexto
                    key={aberturaDaSugestao}
                    discussaoId={id}
                    original={textoParaMelhorar}
                    aoUsar={(texto) => {
                      setCorpo(texto);
                      fecharSugestaoDeTexto();
                    }}
                    aoDescartar={fecharSugestaoDeTexto}
                    aoIndisponivel={() => setIaDeTextoIndisponivel(true)}
                  />
                )}
                {enviarVoz.isPending && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Enviando e transcrevendo o áudio...
                  </div>
                )}
                {gravador.gravando ? (
                  <div
                    role="group"
                    aria-label="Gravando mensagem de voz"
                    className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                  >
                    <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-destructive" />
                    <span className="text-sm tabular-nums text-foreground">
                      {formatarDuracao(gravador.segundos)}
                    </span>
                    {/* Medidor de volume, como no WhatsApp: a pessoa vê na hora
                        se o microfone está pegando a voz. */}
                    <div
                      role="meter"
                      aria-label="Volume do microfone"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(gravador.nivel * 100)}
                      className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-muted"
                    >
                      <div
                        className="h-full rounded-full bg-destructive transition-[width] duration-100"
                        style={{ width: `${Math.max(4, gravador.nivel * 100)}%` }}
                      />
                    </div>
                    {gravador.semSom ? (
                      <span role="status" className="flex-1 truncate text-xs font-medium text-destructive">
                        Não estamos captando som do microfone
                      </span>
                    ) : (
                      <span className="flex-1 truncate text-xs text-muted-foreground">
                        Gravando... (máx. {formatarDuracao(DURACAO_MAXIMA_SEGUNDOS)})
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => void gravador.cancelar()}
                      title="Descartar áudio"
                      aria-label="Descartar áudio"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      onClick={() => void finalizarGravacao()}
                      title="Enviar áudio"
                      aria-label="Enviar áudio"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    // Arrastar PDF/imagem para o composer anexa, como no
                    // WhatsApp Web. Só reage a ARQUIVOS: arrastar texto
                    // selecionado continua caindo no campo normalmente.
                    data-testid="composer"
                    className={cn(
                      'relative flex gap-2 rounded-md transition-shadow',
                      arrastando && 'ring-2 ring-brand ring-offset-2 ring-offset-background',
                    )}
                    onDragEnter={(e) => {
                      if (!arrastoTemArquivos(e.dataTransfer)) return;
                      e.preventDefault();
                      setArrastando(true);
                    }}
                    onDragOver={(e) => {
                      if (!arrastoTemArquivos(e.dataTransfer)) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                    }}
                    onDragLeave={(e) => {
                      // Sair de um filho (o botão, o campo) dispara dragleave
                      // no pai: só apaga o destaque quando sai do composer.
                      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                      setArrastando(false);
                    }}
                    onDrop={(e) => {
                      if (!arrastoTemArquivos(e.dataTransfer)) return;
                      e.preventDefault();
                      setArrastando(false);
                      anexar(Array.from(e.dataTransfer.files));
                    }}
                  >
                    {arrastando && (
                      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md bg-brand/10 text-sm font-medium text-brand">
                        Solte o PDF ou a imagem para anexar
                      </div>
                    )}
                    <input
                      ref={inputArquivoRef}
                      type="file"
                      multiple
                      accept={FORMATOS_DE_ANEXO.join(',')}
                      className="hidden"
                      aria-label="Arquivos para anexar"
                      onChange={(e) => {
                        anexar(Array.from(e.target.files ?? []));
                        // Zera o input: escolher o MESMO arquivo de novo (depois
                        // de removê-lo da lista) não dispararia `change`.
                        e.target.value = '';
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => inputArquivoRef.current?.click()}
                      title="Anexar arquivos ou imagens"
                      aria-label="Anexar arquivos ou imagens"
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Textarea
                      ref={composerRef}
                      value={corpo}
                      onChange={(e) => setCorpo(e.target.value)}
                      disabled={textoParaMelhorar !== null}
                      aria-label="Mensagem"
                      // Enter só quebra linha — quem envia é o botão. Mensagem
                      // clínica costuma ter parágrafos, e o Enter mandava a
                      // metade dela antes de a pessoa terminar de escrever.
                      placeholder="Escreva uma mensagem ou arraste PDFs e imagens..."
                      rows={2}
                      className={CLASSE_CONTROLE}
                    />
                    {!iaDeTextoIndisponivel && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={abrirSugestaoDeTexto}
                        disabled={!corpo.trim() || textoParaMelhorar !== null}
                        title="Melhorar texto com IA"
                        aria-label="Melhorar texto com IA"
                      >
                        <Sparkles className="w-4 h-4 text-brand" />
                      </Button>
                    )}
                    {/* Como no WhatsApp: campo vazio mostra o microfone; com
                      texto (ou anexo) ele vira o botão de enviar. */}
                    {!corpo.trim() && arquivos.length === 0 && gravador.suportado ? (
                      <Button
                        type="button"
                        onClick={() => void gravador.iniciar()}
                        disabled={enviando || textoParaMelhorar !== null}
                        title="Gravar mensagem de voz"
                        aria-label="Gravar mensagem de voz"
                      >
                        {enviando ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mic className="w-4 h-4" />
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => void aoEnviar()}
                        disabled={
                          (!corpo.trim() && arquivos.length === 0) || enviando || textoParaMelhorar !== null
                        }
                        aria-label="Enviar mensagem"
                      >
                        {enviando ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-surface-raised rounded-lg border border-brand/20 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-4 h-4 text-brand" />
                <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
                  Resumo por IA
                </h3>
                {totalMensagens < 3 ? (
                  <Badge variant="outline" className="ml-auto text-xs">
                    Em breve
                  </Badge>
                ) : (
                  (resumoIa.data || resumoIa.isError) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="ml-auto h-6 w-6 text-muted-foreground hover:text-brand"
                      onClick={() => resumoIaMutate({ id })}
                      disabled={resumoIa.isPending}
                      aria-label="Refazer resumo"
                      title="Refazer resumo com as mensagens mais recentes"
                    >
                      <RefreshCw
                        className={`w-3.5 h-3.5 ${resumoIa.isPending ? 'animate-spin' : ''}`}
                      />
                    </Button>
                  )
                )}
              </div>

              {totalMensagens < 3 ? (
                <p className="text-xs text-muted-foreground">
                  Fica disponível assim que a conversa tiver mais mensagens trocadas.
                </p>
              ) : resumoIa.isPending ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Lendo a conversa...
                </div>
              ) : resumoIa.isError ? (
                <p className="text-xs text-muted-foreground">
                  Não foi possível gerar o resumo agora.
                </p>
              ) : resumoIa.data ? (
                <div className="space-y-2.5">
                  <div>
                    <p className="text-xs font-semibold text-brand mb-0.5">Resumo do caso</p>
                    <p className="text-xs text-foreground leading-5">
                      {resumoIa.data.resumoDoCaso}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand mb-0.5">
                      Principais informações clínicas
                    </p>
                    <p className="text-xs text-foreground leading-5">
                      {resumoIa.data.informacoesClinicas}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand mb-0.5">Dúvida principal</p>
                    <p className="text-xs text-foreground leading-5">
                      {resumoIa.data.duvidaPrincipal}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand mb-0.5">Pontos já discutidos</p>
                    {resumoIa.data.pontosDiscutidos.map((ponto, i) => (
                      <p key={i} className="text-xs text-foreground leading-5">
                        • {ponto}
                      </p>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand mb-0.5">Pendências</p>
                    {resumoIa.data.pendencias.map((pendencia, i) => (
                      <p key={i} className="text-xs text-foreground leading-5">
                        • {pendencia}
                      </p>
                    ))}
                  </div>
                  <p className="text-[10.5px] text-muted-foreground leading-4 pt-1">
                    Gerado por IA a partir das mensagens — organiza a informação, não substitui o
                    raciocínio clínico.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="bg-surface-raised rounded-lg border border-brand/20 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-brand uppercase tracking-wide flex items-center gap-1.5">
                  <FlaskConical className="w-4 h-4" />
                  Exames internos
                </h3>
                {discussao.pacienteId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-brand"
                    onClick={() => setEvolucaoAberta(true)}
                  >
                    <LineChart className="w-3.5 h-3.5" />
                    Evolução
                  </Button>
                )}
              </div>
              {examesInternos.length > 0 ? (
                <ul className="space-y-1">
                  {examesInternos.map((exame) => (
                    <li key={exame.id}>
                      <button
                        type="button"
                        onClick={() => setExameInternoId(exame.id)}
                        className="flex items-center gap-1.5 text-sm text-foreground hover:text-brand transition-colors text-left"
                      >
                        <FlaskConical className="w-3.5 h-3.5 shrink-0" />
                        {exame.exameNome}
                        {exame.os && (
                          <span className="text-muted-foreground"> — OS {exame.os}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum exame interno anexado.</p>
              )}
            </div>

            <div className="bg-surface-raised rounded-lg border border-brand/20 p-4">
              <h3 className="text-sm font-semibold text-brand uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <FlaskConical className="w-4 h-4" />
                Exames externos
              </h3>
              {examesExternos.length > 0 ? (
                <ul className="space-y-1">
                  {examesExternos.map((exame) => (
                    <li key={exame.id}>
                      <button
                        type="button"
                        onClick={() => void aoAbrirExame(exame.id)}
                        disabled={abrindoExameId === exame.id}
                        className="flex items-center gap-1.5 text-sm text-foreground hover:text-brand transition-colors disabled:opacity-60"
                      >
                        {abrindoExameId === exame.id ? (
                          <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" />
                        ) : (
                          <Paperclip className="w-3.5 h-3.5 shrink-0" />
                        )}
                        {exame.exameNome}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum exame externo anexado.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {discussao.avaliacaoPendente && (
        <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-4 flex items-center justify-between">
          <p className="text-sm text-foreground">Como foi o atendimento deste advisor?</p>
          <Button size="sm" onClick={() => setAvaliando(true)}>
            <Star className="w-4 h-4" />
            Avaliar
          </Button>
        </div>
      )}

      {/* Rodapé: artigos publicados do advisor. Some sozinho quando não há nenhum. */}
      {discussao.assessorId && (
        <ArtigosDoAdvisor discussaoId={id} assessorNome={discussao.assessorNome ?? null} />
      )}

      <HistoricoDoPacienteDialogo
        discussaoId={id}
        aberto={historicoAberto}
        aoFechar={() => setHistoricoAberto(false)}
      />

      <ResultadosDoExameDialogo exameId={exameInternoId} aoFechar={() => setExameInternoId(null)} />

      <EvolucaoDoPacienteDialogo
        discussaoId={id}
        aberto={evolucaoAberta}
        aoFechar={() => setEvolucaoAberta(false)}
      />

      <AlertDialog open={confirmandoEncerrar} onOpenChange={setConfirmandoEncerrar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar esta discussão?</AlertDialogTitle>
            <AlertDialogDescription>
              Ninguém poderá mandar mensagem depois disso. A ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void aoEncerrar()}>Encerrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DialogoAvaliacao aberto={avaliando} aoFechar={() => setAvaliando(false)} discussaoId={id} />

      <VisualizadorDeDocumento
        aberto={visualizando !== null}
        aoFechar={() => setVisualizando(null)}
        url={visualizando?.url ?? null}
        mimeType={visualizando?.mimeType ?? null}
      />
    </div>
  );
}

/**
 * RASCUNHO na tela de detalhe: o médico pode ter saído no meio do wizard (a
 * partir da etapa "Exames a compartilhar", quando o rascunho já existe no
 * servidor — ver `AberturaDiscussaoWizard`). Retoma exatamente de onde parou
 * em vez de já cair na tela de pagamento, que só faz sentido depois do
 * último passo ("Enviar solicitação de discussão").
 *
 * `useFluxoDeAberturaDeDiscussao(false)`: mesma checagem de crédito +
 * despacho de `NovaDiscussaoPage`, `doApp` sempre falso aqui — não existe
 * caminho do navegador in-app do celular pra a tela de DETALHE de uma
 * discussão específica.
 */
function RascunhoDaDiscussao({ discussaoId }: { discussaoId: string }) {
  const { fase, mensagensPendentes, aoAbrir } = useFluxoDeAberturaDeDiscussao(false);
  const {
    data: rascunho,
    isPending,
    isError,
    refetch,
  } = useDiscussoesControllerObterRascunho(discussaoId, { query: { enabled: fase === 'preenchendo' } });

  if (fase === 'liquidando') {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
        <p className="text-sm text-muted-foreground">Abrindo a discussão...</p>
      </div>
    );
  }

  if (fase === 'pagamento') {
    return (
      <>
        <p className="text-sm text-muted-foreground mb-4">
          O pagamento desta discussão ainda não foi confirmado.
        </p>
        <EtapaPagamento discussaoId={discussaoId} mensagensPendentes={mensagensPendentes} />
      </>
    );
  }

  if (isPending) return <EstadoCarregando />;
  if (isError || !rascunho) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return <AberturaDiscussaoWizard aoAbrir={aoAbrir} rascunho={rascunho} />;
}

function DialogoAvaliacao({
  aberto,
  aoFechar,
  discussaoId,
}: {
  aberto: boolean;
  aoFechar: () => void;
  discussaoId: string;
}) {
  const avaliar = useDiscussoesControllerAvaliar();
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormularioAvaliacao>({
    resolver: zodResolver(schemaAvaliacao),
    defaultValues: { nota: 5, resolvido: 'sim', descricao: '' },
  });

  useEffect(() => {
    if (aberto) reset({ nota: 5, resolvido: 'sim', descricao: '' });
  }, [aberto, reset]);

  const aoEnviar = handleSubmit(async (bruto) => {
    const dados = schemaAvaliacao.parse(bruto);

    try {
      await avaliar.mutateAsync({
        id: discussaoId,
        data: {
          nota: dados.nota,
          resolvido: dados.resolvido === 'sim',
          ...(dados.descricao.trim() ? { descricao: dados.descricao.trim() } : {}),
        },
      });
      toast.success('Avaliação enviada. Obrigado!');
      aoFechar();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível enviar a avaliação.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Avaliar atendimento</DialogTitle>
          <DialogDescription>Sua nota ajuda a manter a qualidade dos advisors.</DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4">
          <Campo id="nota" rotulo="Nota (1 a 5)" obrigatorio>
            <input
              id="nota"
              type="number"
              min={1}
              max={5}
              className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm ${CLASSE_CONTROLE}`}
              {...register('nota')}
            />
          </Campo>

          <Campo id="resolvido" rotulo="A dúvida foi resolvida?" obrigatorio>
            <Controller
              name="resolvido"
              control={control}
              render={({ field }) => (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={field.value === 'sim' ? 'default' : 'outline'}
                    onClick={() => field.onChange('sim')}
                    className="flex-1"
                  >
                    Sim
                  </Button>
                  <Button
                    type="button"
                    variant={field.value === 'nao' ? 'default' : 'outline'}
                    onClick={() => field.onChange('nao')}
                    className="flex-1"
                  >
                    Não
                  </Button>
                </div>
              )}
            />
          </Campo>

          <Campo id="descricao" rotulo="Comentário">
            <Textarea
              id="descricao"
              rows={3}
              className={CLASSE_CONTROLE}
              {...register('descricao')}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enviar avaliação
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
